import { useState } from 'react';
import { useAppStore } from '../../services/store';

interface Review {
  id: string;
  appId: string;
  appName: string;
  userName: string;
  userEmail: string;
  rating: number;
  comment: string;
  date: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  adminNotes?: string;
}

interface ReviewManagementProps {
  filter?: string;
}

export default function ReviewManagement({ filter: propFilter = 'All' }: ReviewManagementProps) {
  const { apps } = useAppStore();
  const [filter, setFilter] = useState(propFilter);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [adminNotes, setAdminNotes] = useState('');

  // Sample reviews data - in real app this would come from your store
  const [reviews, setReviews] = useState<Review[]>([
    {
      id: '1',
      appId: '1',
      appName: 'Document Management',
      userName: 'John Smith',
      userEmail: 'john.smith@company.com',
      rating: 5,
      comment: 'Excellent app for managing our documents. Very user-friendly interface and great search functionality.',
      date: '2024-01-15',
      status: 'Pending'
    },
    {
      id: '2',
      appId: '3',
      appName: 'OpsMonitor',
      userName: 'Sarah Johnson',
      userEmail: 'sarah.j@company.com',
      rating: 4,
      comment: 'Great monitoring capabilities. Would love to see more customization options for dashboards.',
      date: '2024-01-14',
      status: 'Approved'
    },
    {
      id: '3',
      appId: '1',
      appName: 'Document Management',
      userName: 'Mike Wilson',
      userEmail: 'mike.w@company.com',
      rating: 2,
      comment: 'App crashes frequently and the interface is confusing. Needs major improvements.',
      date: '2024-01-13',
      status: 'Pending'
    }
  ]);

  const filteredReviews = reviews.filter(review => {
    if (filter === 'All' || filter === 'all') return true;
    if (filter === 'pending') return review.status === 'Pending';
    if (filter === 'approved' || filter === 'accepted') return review.status === 'Approved';
    if (filter === 'rejected') return review.status === 'Rejected';
    return review.status === filter;
  });

  const handleAction = (review: Review, action: 'approve' | 'reject') => {
    setSelectedReview(review);
    setActionType(action);
    setAdminNotes('');
    setShowModal(true);
  };

  const handleSubmitAction = () => {
    if (!selectedReview) return;

    const newStatus = actionType === 'approve' ? 'Approved' : 'Rejected';
    const updatedReviews = reviews.map(review =>
      review.id === selectedReview.id
        ? { ...review, status: newStatus, adminNotes }
        : review
    );

    setReviews(updatedReviews);
    setShowModal(false);
    setSelectedReview(null);
    setAdminNotes('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`ri-star-${star <= rating ? 'fill' : 'line'} text-yellow-400 text-sm`}
          ></i>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Review Management ({filteredReviews.length} reviews)
        </h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 pr-8"
        >
          <option value="All">All Reviews</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {filteredReviews.map((review) => (
            <div key={review.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h4 className="text-lg font-semibold text-gray-900">{review.appName}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(review.status)}`}>
                      {review.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mb-3">
                    {renderStars(review.rating)}
                    <span className="text-sm text-gray-500">•</span>
                    <span className="text-sm text-gray-600">by {review.userName}</span>
                    <span className="text-sm text-gray-500">•</span>
                    <span className="text-sm text-gray-500">{new Date(review.date).toLocaleDateString()}</span>
                  </div>

                  <p className="text-sm text-gray-700 mb-4 leading-relaxed bg-gray-50 p-4 rounded-lg">
                    "{review.comment}"
                  </p>

                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Reviewer Email:</span> {review.userEmail}
                  </div>

                  {review.adminNotes && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h5 className="font-medium text-blue-900 mb-2">Admin Notes</h5>
                      <p className="text-sm text-blue-800">{review.adminNotes}</p>
                    </div>
                  )}
                </div>

                {review.status === 'Pending' && (
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleAction(review, 'approve')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      <i className="ri-check-line mr-1"></i>
                      Approve
                    </button>
                    <button
                      onClick={() => handleAction(review, 'reject')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      <i className="ri-close-line mr-1"></i>
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredReviews.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-star-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews found</h3>
            <p className="text-gray-600">
              {filter === 'All' ? 'No reviews have been submitted yet' : `No ${filter.toLowerCase()} reviews found`}
            </p>
          </div>
        )}
      </div>

      {showModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {actionType === 'approve' ? 'Approve Review' : 'Reject Review'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {selectedReview.appName} review by {selectedReview.userName}
              </p>
            </div>

            <div className="p-6">
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  {renderStars(selectedReview.rating)}
                </div>
                <p className="text-sm text-gray-700">"{selectedReview.comment}"</p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes {actionType === 'reject' ? '*' : '(Optional)'}
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={
                  actionType === 'approve'
                    ? "Add any notes about this approval..."
                    : "Please provide a reason for rejection..."
                }
                required={actionType === 'reject'}
              />
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAction}
                disabled={actionType === 'reject' && !adminNotes.trim()}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium whitespace-nowrap ${
                  actionType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
